// src/components/LiveTaskOutput.jsx
// Renders intermediate findings under each timeline task while the autopilot
// is running. Pulls from the `areaOutputs[areaKey]` payload that the
// discovery/evaluation/action/residual services push via `liveUpdateCallback`.
// Used by Builder + Explorer autopilots.

import React from 'react';
import { Paper, Text, List } from '@mantine/core';

export default function LiveTaskOutput({ task, outputs }) {
  if (!outputs) return null;

  switch (task) {
    case 'analyzing risk area': {
      const summary = outputs.areaCard?.summary;
      const analysis = outputs.areaCard?.analysis;
      const text = (Array.isArray(summary) && summary[0]) || analysis;
      if (!text) return null;
      return (
        <Paper p="xs" mt="xs" withBorder bg="gray.0">
          <Text size="xs" c="dark.6" lineClamp={3}>
            {text}
          </Text>
        </Paper>
      );
    }

    case 'researching risk sources': {
      const sources = outputs.selectedSources || [];
      if (sources.length === 0) return null;
      return (
        <Paper p="xs" mt="xs" withBorder bg="gray.0">
          <Text size="xs" fw={600} mb={4}>
            {sources.length} source{sources.length === 1 ? '' : 's'} identified
          </Text>
          <List spacing={2} size="xs" listStyleType="disc" withPadding>
            {sources.slice(0, 5).map((s, i) => (
              <List.Item key={i}>
                <Text size="xs" lineClamp={1}>
                  {s.sourceName || s.name || 'Source'}
                </Text>
              </List.Item>
            ))}
            {sources.length > 5 && (
              <Text size="xs" c="dimmed" mt={2}>
                + {sources.length - 5} more
              </Text>
            )}
          </List>
        </Paper>
      );
    }

    case 'researching root causes': {
      const causesBySource = outputs.causesBySource || {};
      const allCauses = Object.values(causesBySource).flatMap((bucket) =>
        Array.isArray(bucket)
          ? bucket.flatMap((cat) => cat.researchedCauses || [])
          : []
      );
      if (allCauses.length === 0) return null;
      return (
        <Paper p="xs" mt="xs" withBorder bg="gray.0">
          <Text size="xs" fw={600} mb={4}>
            {allCauses.length} root cause{allCauses.length === 1 ? '' : 's'} found
          </Text>
          <List spacing={2} size="xs" listStyleType="disc" withPadding>
            {allCauses.slice(0, 4).map((c, i) => (
              <List.Item key={i}>
                <Text size="xs" lineClamp={1}>
                  {c.name || c.causeName || 'Cause'}
                </Text>
              </List.Item>
            ))}
            {allCauses.length > 4 && (
              <Text size="xs" c="dimmed" mt={2}>
                + {allCauses.length - 4} more
              </Text>
            )}
          </List>
        </Paper>
      );
    }

    case 'preparing causes for RAG': {
      const relevant = outputs.relevantCauses || [];
      if (relevant.length === 0) return null;
      return (
        <Paper p="xs" mt="xs" withBorder bg="gray.0">
          <Text size="xs" c="dark.6">
            {relevant.length} high-confidence cause{relevant.length === 1 ? '' : 's'} selected for retrieval
          </Text>
        </Paper>
      );
    }

    case 'scenarios': {
      const scenarios = outputs.scenarios || [];
      const cataloguesUsed = outputs.ragMetadata?.cataloguesUsed || [];
      if (scenarios.length === 0) return null;
      return (
        <Paper p="xs" mt="xs" withBorder bg="gray.0">
          <Text size="xs" fw={600} mb={4}>
            {scenarios.length} risk{scenarios.length === 1 ? '' : 's'} drafted
            {cataloguesUsed.length > 0 && (
              <Text component="span" size="xs" c="dimmed" fw={400}>
                {' · grounded in '}
                {cataloguesUsed.slice(0, 2).join(', ')}
                {cataloguesUsed.length > 2 ? ` + ${cataloguesUsed.length - 2}` : ''}
              </Text>
            )}
          </Text>
          <List spacing={2} size="xs" listStyleType="disc" withPadding>
            {scenarios.slice(0, 3).map((s, i) => (
              <List.Item key={i}>
                <Text size="xs" lineClamp={1}>
                  {s.scenarioText || s.title || s.description || 'Scenario'}
                </Text>
              </List.Item>
            ))}
            {scenarios.length > 3 && (
              <Text size="xs" c="dimmed" mt={2}>
                + {scenarios.length - 3} more
              </Text>
            )}
          </List>
        </Paper>
      );
    }

    case 'evaluation': {
      const evals = outputs.riskEvaluations || [];
      if (evals.length === 0) return null;
      const latest = evals[evals.length - 1] || {};
      return (
        <Paper p="xs" mt="xs" withBorder bg="gray.0">
          <Text size="xs" fw={600} mb={4}>
            {outputs.evaluationProgress || `${evals.length} prioritized`}
          </Text>
          {latest.riskName && (
            <Text size="xs" lineClamp={1}>
              Latest: <Text component="span" fw={600}>{latest.priority || '—'}</Text> · {latest.riskName}
            </Text>
          )}
        </Paper>
      );
    }

    case 'actions': {
      const updates = outputs.controlUpdates || [];
      if (updates.length === 0) return null;
      const totalControls = updates.reduce(
        (n, u) => n + (u.controlsCount || u.controls?.length || 0),
        0
      );
      return (
        <Paper p="xs" mt="xs" withBorder bg="gray.0">
          <Text size="xs" c="dark.6">
            {totalControls > 0
              ? `${totalControls} controls drafted across ${updates.length} risk${updates.length === 1 ? '' : 's'}`
              : `${updates.length} risk${updates.length === 1 ? '' : 's'} processed`}
          </Text>
        </Paper>
      );
    }

    case 'residual': {
      const residuals = outputs.residualCalculations || [];
      if (residuals.length === 0) return null;
      const latest = residuals[residuals.length - 1] || {};
      return (
        <Paper p="xs" mt="xs" withBorder bg="gray.0">
          <Text size="xs" fw={600} mb={4}>
            {outputs.residualProgress || `${residuals.length} residual${residuals.length === 1 ? '' : 's'} calculated`}
          </Text>
          {latest.riskName && (
            <Text size="xs" lineClamp={1}>
              Latest: <Text component="span" fw={600}>{latest.residualPriority || latest.priority || '—'}</Text> · {latest.riskName}
            </Text>
          )}
        </Paper>
      );
    }

    default:
      return null;
  }
}
